<template>
  <div class="file-upload-zone">
    <!-- Single file selected: show file card -->
    <div v-if="!multiple && selectedFile" class="file-card" :class="{ 'file-card-labelled': actionLabels, 'file-card-narrow': compact }">
      <div class="file-card-icon">
        <i :class="fileIcon(selectedFile)"></i>
      </div>
      <div class="file-card-info">
        <span class="file-card-name">{{ selectedFile.name }}</span>
        <span class="file-card-size">{{ formatSize(selectedFile.size) }}</span>
      </div>
      <!-- `actionLabels` : les deux actions portent leur mot à côté de l'icône.
           Un écran public n'a pas d'icône cliquable sans libellé (P1 de
           docs/PUBLIQUES-V2.md) : le visiteur n'a jamais vu l'outil. -->
      <div v-if="actionLabels" class="file-card-actions">
        <button type="button" class="file-card-btn file-card-btn-labelled replace" data-testid="file-upload-replace" :disabled="disabled || uploading" @click="openPicker">
          <i class="fas fa-sync-alt" aria-hidden="true"></i>
          <span>Remplacer</span>
        </button>
        <button type="button" class="file-card-btn file-card-btn-labelled remove" data-testid="file-upload-remove" :disabled="disabled || uploading" @click="handleRemoveFile">
          <i class="fas fa-trash-alt" aria-hidden="true"></i>
          <span>Retirer</span>
        </button>
      </div>
      <div v-else class="file-card-actions">
        <Tooltip content="Remplacer le fichier" placement="top">
          <button type="button" class="file-card-btn replace" aria-label="Remplacer le fichier" data-testid="file-upload-replace" :disabled="disabled || uploading" @click="openPicker">
            <i class="fas fa-sync-alt" aria-hidden="true"></i>
          </button>
        </Tooltip>
        <Tooltip content="Retirer le fichier" placement="top">
          <button type="button" class="file-card-btn remove" aria-label="Retirer le fichier" data-testid="file-upload-remove" :disabled="disabled || uploading" @click="handleRemoveFile">
            <i class="fas fa-trash-alt" aria-hidden="true"></i>
          </button>
        </Tooltip>
      </div>
    </div>

    <!-- Multiple files selected: show file list -->
    <div v-if="multiple && selectedFiles && selectedFiles.length > 0" class="file-list">
      <div
        v-for="(file, idx) in selectedFiles"
        :key="idx"
        class="file-card file-card-compact"
        :class="{ 'file-card-labelled': actionLabels, 'file-card-narrow': compact }"
      >
        <div class="file-card-icon">
          <i :class="fileIcon(file)"></i>
        </div>
        <div class="file-card-info">
          <span class="file-card-name">{{ file.name }}</span>
          <span class="file-card-size">{{ formatSize(file.size) }}</span>
        </div>
        <!-- Plusieurs « Retirer » à la suite ne se distinguent, pour un lecteur
             d'écran, que par le nom du fichier : le nom accessible le porte, et
             commence par le mot affiché. -->
        <div v-if="actionLabels" class="file-card-actions">
          <button type="button" class="file-card-btn file-card-btn-labelled remove" :aria-label="`Retirer ${file.name}`" data-testid="file-upload-remove" :disabled="disabled" @click="handleRemoveFileAt(idx)">
            <i class="fas fa-trash-alt" aria-hidden="true"></i>
            <span>Retirer</span>
          </button>
        </div>
        <Tooltip v-else :content="`Retirer ${file.name}`" placement="top">
          <button type="button" class="file-card-btn remove" :aria-label="`Retirer ${file.name}`" data-testid="file-upload-remove" :disabled="disabled" @click="handleRemoveFileAt(idx)">
            <i class="fas fa-times" aria-hidden="true"></i>
          </button>
        </Tooltip>
      </div>
    </div>

    <!-- Drop zone: hidden when single file selected, always visible for multiple -->
    <div
      v-if="multiple || !selectedFile"
      class="drop-zone"
      :class="{ 'drag-over': isDragging, 'disabled': disabled, 'drop-zone-compact': compact || (multiple && selectedFiles && selectedFiles.length > 0) }"
      @dragenter.prevent="handleDragEnter"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
    >
      <div class="drop-zone-content">
        <template v-if="multiple && selectedFiles && selectedFiles.length > 0">
          <button ref="pickerButton" type="button" class="upload-label upload-label-compact" :class="{ 'disabled': disabled }" :disabled="disabled || uploading" @click="openPicker">
            <i class="fas fa-plus-circle" aria-hidden="true"></i>
            Ajouter d'autres fichiers
          </button>
        </template>
        <template v-else>
          <i :class="isDragging ? 'fas fa-cloud-upload-alt drop-icon-active' : `${icon} drop-icon`"></i>
          <p class="drop-text">{{ isDragging ? dropActiveText : dropText }}</p>
          <span class="drop-separator">ou</span>
          <button ref="pickerButton" type="button" class="upload-label" :class="{ 'disabled': disabled }" :disabled="disabled || uploading" @click="openPicker">
            <i class="fas fa-plus-circle" aria-hidden="true"></i>
            {{ buttonLabel }}
          </button>
          <p v-if="hint" class="upload-hint">
            <i class="fas fa-info-circle"></i>
            {{ hint }}
          </p>
        </template>
      </div>
    </div>

    <!-- Les actions « Choisir », « Remplacer » et « Ajouter » sont de vrais
         boutons qui ouvrent ce sélecteur : c'étaient des <label for>, hors de
         l'ordre de tabulation, et l'input masqué ne l'est pas davantage — sans
         souris, on ne pouvait déposer aucun fichier. -->
    <input
      :id="inputId"
      ref="fileInput"
      :data-testid="inputTestId || undefined"
      type="file"
      :accept="accept"
      :multiple="multiple"
      :disabled="disabled || uploading"
      style="display: none"
      @change="handleFileSelect"
    />

    <div v-if="uploading" class="upload-status uploading">
      <i class="fas fa-spinner fa-spin"></i>
      {{ uploadingText }}
    </div>

    <div v-if="error" class="upload-status error">
      <i class="fas fa-exclamation-circle"></i>
      {{ error }}
    </div>
  </div>
</template>

<script setup>
import { nextTick, ref, watch } from 'vue'
import { toast } from 'vue-sonner'
import Tooltip from './Tooltip.vue'

const props = defineProps({
  /** Zone réduite : icône et rembourrage discrets, pour une colonne étroite. */
  compact: {
    type: Boolean,
    default: false
  },
  accept: {
    type: String,
    default: '.pdf,application/pdf'
  },
  maxSize: {
    type: Number,
    default: 10 * 1024 * 1024 // 10MB
  },
  multiple: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  uploading: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: null
  },
  icon: {
    type: String,
    default: 'fas fa-file-pdf'
  },
  dropText: {
    type: String,
    default: 'Glissez un fichier ici'
  },
  dropActiveText: {
    type: String,
    default: 'Déposez votre fichier ici'
  },
  buttonLabel: {
    type: String,
    default: 'Ajouter un document'
  },
  uploadingText: {
    type: String,
    default: 'Upload en cours...'
  },
  hint: {
    type: String,
    default: null
  },
  maxSizeLabel: {
    type: String,
    default: null
  },
  acceptLabel: {
    type: String,
    default: null
  },
  selectedFile: {
    type: [File, Object],
    default: null
  },
  selectedFiles: {
    type: Array,
    default: null
  },
  /**
   * Repère de test posé sur l'`<input type="file">` lui-même. Il n'y en avait
   * pas : une page qui remplaçait sa zone de dépôt maison par celle du kit
   * perdait le sélecteur avec lequel ses tests déposaient un fichier.
   */
  inputTestId: {
    type: String,
    default: null
  },
  /**
   * « Remplacer » et « Retirer » écrits à côté de leur icône, sans infobulle.
   * Par défaut l'icône seule (le back-office, où la place compte) ; un écran
   * public le demande, parce qu'une icône nue ne dit rien à un visiteur.
   */
  actionLabels: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['file-selected', 'files-selected', 'file-removed', 'file-removed-at'])

const isDragging = ref(false)
const fileInput = ref(null)
const inputId = `file-upload-${Math.random().toString(36).substr(2, 9)}`
let dragCounter = 0

const formatSize = (bytes) => {
  if (!bytes) return ''
  if (bytes < 1024) return bytes + ' o'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko'
  return (bytes / (1024 * 1024)).toFixed(1) + ' Mo'
}

const fileIcon = (file) => {
  if (!file?.name) return 'fas fa-file'
  const ext = file.name.split('.').pop().toLowerCase()
  if (ext === 'pdf') return 'fas fa-file-pdf'
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'fas fa-file-image'
  if (['doc', 'docx'].includes(ext)) return 'fas fa-file-word'
  return 'fas fa-file'
}

const openPicker = () => {
  if (props.disabled || props.uploading) return
  fileInput.value?.click()
}

/*
 * Le bouton « Retirer » disparaît avec le fichier qu'il retire : le focus
 * retombait sur <body>, et au clavier on repartait du haut de la page. Une fois
 * le retrait appliqué par le parent, il se pose sur le bouton qui permet d'en
 * choisir un autre. Le parent peut refuser ou différer le retrait : on n'agit
 * que sur le changement qui suit le clic, et seulement si le focus est perdu.
 */
const pickerButton = ref(null)
let retraitDemande = false

const focusApresRetrait = async () => {
  if (!retraitDemande) return
  retraitDemande = false
  await nextTick()
  const actif = document.activeElement
  if (actif && actif !== document.body && actif.isConnected) return
  pickerButton.value?.focus()
}

watch(() => props.selectedFile, (fichier) => { if (!fichier) focusApresRetrait() })
watch(() => props.selectedFiles?.length ?? 0, (n, avant) => { if (n < avant) focusApresRetrait() })

const handleRemoveFile = () => {
  if (props.disabled || props.uploading) return
  retraitDemande = true
  emit('file-removed')
}

/*
 * Dans une liste, retirer un fichier reste permis pendant qu'un autre monte :
 * les appelants savent annuler un fichier encore en route (cf. les suppressions
 * différées de useFormateurDocumentsPublics). Seule une zone `disabled` bloque.
 */
const handleRemoveFileAt = (idx) => {
  if (props.disabled) return
  retraitDemande = true
  emit('file-removed-at', idx)
}

const getAcceptedExtensions = () => {
  return props.accept.split(',').map(t => t.trim().toLowerCase())
}

const validateFile = (file) => {
  const accepted = getAcceptedExtensions()
  const fileExt = '.' + file.name.split('.').pop().toLowerCase()
  const matchesType = accepted.some(a =>
    file.type === a || fileExt === a || a === '*/*'
  )

  if (!matchesType) {
    const label = props.acceptLabel || `Formats acceptés : ${accepted.filter(a => a.startsWith('.')).join(', ')}`
    toast.error(label)
    return false
  }

  if (file.size > props.maxSize) {
    const sizeMB = Math.round(props.maxSize / (1024 * 1024))
    const label = props.maxSizeLabel || `Le fichier est trop volumineux (maximum ${sizeMB}MB)`
    toast.error(label)
    return false
  }

  return true
}

const handleFileSelect = (event) => {
  const files = Array.from(event.target.files)
  if (files.length === 0) return

  const validFiles = files.filter(validateFile)
  if (validFiles.length === 0) {
    event.target.value = ''
    return
  }

  if (props.multiple) {
    emit('files-selected', validFiles)
  } else {
    emit('file-selected', validFiles[0])
  }
  event.target.value = ''
}

const handleDragEnter = () => {
  if (props.disabled) return
  dragCounter++
  isDragging.value = true
}

const handleDragOver = (event) => {
  if (props.disabled) return
  event.dataTransfer.dropEffect = 'copy'
}

const handleDragLeave = () => {
  dragCounter--
  if (dragCounter === 0) {
    isDragging.value = false
  }
}

const handleDrop = (event) => {
  dragCounter = 0
  isDragging.value = false
  if (props.disabled) return

  const files = Array.from(event.dataTransfer.files)
  if (files.length === 0) return

  if (props.multiple) {
    const validFiles = files.filter(validateFile)
    if (validFiles.length > 0) {
      emit('files-selected', validFiles)
    }
  } else {
    const file = files[0]
    if (validateFile(file)) {
      emit('file-selected', file)
    }
  }
}
</script>

<style scoped>
.file-upload-zone {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.drop-zone {
  border: 2px dashed var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--spacing-xl);
  transition: all var(--transition-base);
  background: var(--color-bg-secondary);
}

.drop-zone.drag-over {
  border-color: var(--color-primary);
  background: rgba(59, 130, 246, 0.05);
  transform: scale(1.01);
}

.drop-zone.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.drop-zone-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  text-align: center;
}

.drop-icon {
  font-size: 2.5rem;
  color: var(--color-text-tertiary);
  transition: all var(--transition-base);
}

.drop-icon-active {
  font-size: 3rem;
  color: var(--color-primary);
  animation: bounce 0.5s ease infinite alternate;
}

@keyframes bounce {
  from { transform: translateY(0); }
  to { transform: translateY(-8px); }
}

.drop-text {
  margin: 0;
  font-size: 0.95rem;
  color: var(--color-text-secondary);
}

.drag-over .drop-text {
  color: var(--color-primary);
  font-weight: 500;
}

.drop-separator {
  font-size: 0.85rem;
  color: var(--color-text-tertiary);
  text-transform: uppercase;
}

.upload-label {
  font: inherit;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--color-primary);
  color: white;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-base);
  font-weight: 500;
}

/* Un bouton désactivé (zone `disabled`, ou envoi en cours) ne réagit ni au
   survol ni à l'appui : un fond de survol sur un bouton grisé le disait
   cliquable. */
.upload-label:hover:not(.disabled):not(:disabled) {
  background: var(--color-primary-dark);
}

.upload-label:active:not(.disabled):not(:disabled) {
  transform: scale(0.98);
}

.upload-label.disabled,
.upload-label:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.upload-label i {
  font-size: 1.1rem;
}

.upload-hint {
  margin: 0;
  margin-top: var(--spacing-sm);
  font-size: 0.85rem;
  color: var(--color-text-tertiary);
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-style: italic;
}

.upload-hint i {
  font-size: 0.9rem;
  color: var(--color-info, #0ea5e9);
}

/* File card (uploaded state) */
.file-card {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md) var(--spacing-lg);
  border: 1px solid var(--color-success, #22c55e);
  border-radius: var(--radius-lg);
  background: rgba(34, 197, 94, 0.05);
}

.file-card-compact {
  padding: var(--spacing-sm) var(--spacing-md);
  margin-bottom: var(--spacing-xs, 4px);
}

.file-card-icon {
  font-size: 1.4rem;
  color: var(--color-success, #22c55e);
  flex-shrink: 0;
  width: 32px;
  text-align: center;
}

.file-card-compact .file-card-icon {
  font-size: 1.1rem;
  width: 24px;
}

.file-card-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.file-card-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-card-compact .file-card-name {
  font-size: 0.85rem;
}

.file-card-size {
  font-size: 0.78rem;
  color: var(--color-text-tertiary);
}

.file-card-actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs, 4px);
  flex-shrink: 0;
}

/* Le wrapper de Tooltip s'étire en pleine largeur par défaut : dans une rangée
   d'actions, il doit rester à la taille du bouton qu'il enveloppe. */
.file-card :deep(.tooltip-wrapper) {
  display: inline-flex;
  width: auto;
}

.file-card-btn {
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
  font-size: 0.85rem;
  background: transparent;
}

.file-card-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.file-card-btn-labelled {
  width: auto;
  min-height: 44px;
  gap: var(--spacing-2);
  padding: 0 var(--spacing-3);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

.file-card-btn.replace {
  color: var(--color-text-tertiary);
}

.file-card-btn.replace:hover:not(:disabled) {
  background: var(--color-bg-secondary);
  color: var(--color-primary);
}

.file-card-btn.remove {
  color: var(--color-text-tertiary);
}

.file-card-btn.remove:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.1);
  color: var(--color-danger, #ef4444);
}

/* Zone `compact` avec les actions en mots : « Remplacer » et « Retirer »
   (224 px à eux deux) ne tiennent pas à côté du nom dans une colonne de
   260 px. Le nom garde au moins 8rem ; quand la place manque, les actions
   passent dessous (à gauche) au lieu de déborder, et peuvent encore se séparer
   sur un très petit écran. Dans une zone large, rien ne bouge. */
.file-card-labelled.file-card-narrow {
  flex-wrap: wrap;
  row-gap: var(--spacing-2);
}

.file-card-labelled.file-card-narrow .file-card-info {
  flex-basis: 8rem;
}

.file-card-labelled.file-card-narrow .file-card-actions {
  flex-wrap: wrap;
  max-width: 100%;
  margin-left: 0;
}

.file-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs, 4px);
  margin-bottom: var(--spacing-sm);
}

/* Compact drop zone for adding more files */
.drop-zone-compact {
  padding: var(--spacing-md);
  border-style: dashed;
}

/* En version compacte, la zone se réduit à ce qu'elle dit : une invite, la
   mise en garde, un bouton. L'icône au repos disparaît — elle ne fait que
   répéter le texte — mais celle du survol reste, c'est elle qui confirme que
   le fichier peut être lâché. */
.drop-zone-compact {
  border-width: 1px;
  border-color: var(--color-border-dark);
  border-radius: var(--radius-md);
}

.drop-zone-compact .drop-icon {
  display: none;
}

.drop-zone-compact .drop-icon-active {
  font-size: 1.5rem;
}

.drop-zone-compact .drop-text {
  font-size: var(--font-size-sm);
  margin: 0;
}

.drop-zone-compact .drop-separator {
  display: none;
}

.drop-zone-compact .upload-label {
  font-size: var(--font-size-sm);
  padding: var(--spacing-2) var(--spacing-4);
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  box-shadow: none;
}

.drop-zone-compact .upload-label:hover:not(.disabled):not(:disabled) {
  background: var(--color-bg-hover);
  border-color: var(--color-border-dark);
  color: var(--color-text-primary);
}

.drop-zone-compact .upload-label i {
  display: none;
}

/* La mise en garde se lit juste après l'invite et avant le bouton : c'est
   elle qui décide si on dépose le fichier. Ni icône ni italique — ce n'est
   pas une alerte, c'est une précision. */
.drop-zone-compact .upload-label {
  order: 1;
}

.drop-zone-compact .upload-hint {
  margin: 2px 0 var(--spacing-3);
  font-size: var(--font-size-xs);
  font-style: normal;
}

.drop-zone-compact .upload-hint i {
  display: none;
}

.drop-zone-compact .drop-zone-content {
  gap: 0;
}

.upload-label-compact {
  font-size: 0.85rem;
  padding: var(--spacing-sm) var(--spacing-md);
}

/* Anneau de focus : en DERNIER, et au-dessus de la variante compacte. La règle
   `.drop-zone-compact .upload-label { box-shadow: none }` avait la même
   spécificité et venait après : elle effaçait l'anneau, et l'outline du
   navigateur étant supprimé, « Déposer » ou « Choisir un fichier » prenaient
   le focus sans que rien ne se voie. */
.upload-label:focus-visible,
.drop-zone-compact .upload-label:focus-visible,
.file-card-btn:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-strong);
}

.upload-status {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--radius-sm);
  font-size: 0.9rem;
}

.upload-status.uploading {
  background: var(--color-info-lighter, #e0f2fe);
  border-left: 3px solid var(--color-info, #0ea5e9);
  color: var(--color-info-dark, #0369a1);
}

.upload-status.error {
  background: var(--color-danger-lighter, #fee2e2);
  border-left: 3px solid var(--color-danger, #ef4444);
  color: var(--color-danger-dark, #991b1b);
}
</style>

